/**
 * CENTRALIZED DOMICILIATION PERMISSIONS MODULE
 * 
 * This module contains ALL the logic for domiciliation permissions.
 * It handles both token-based permissions (call-center/anonymously/agente)
 * and client-based permissions (cliente).
 * 
 * Usage:
 * - For token-based: checkDomiciliationPermission(specialToken)
 * - For cliente: checkDomiciliationPermission()
 */

import axios from 'axios';
import get from 'lodash/get';
import { 
  DOMICILIATION_PERMISSIONS_CHECK_SPECIAL, 
  DOMICILIATION_PERMISSIONS_CLIENTE 
} from '../constants/endpoints';
import { USER_TYPES } from '../constants/permissions';

/**
 * Cache for permissions to avoid redundant API calls
 * Key format: 'token:{token}' or 'userType:{userType}'
 */
const permissionsCache = new Map();

/**
 * Clear the permissions cache
 * Useful for testing or when permissions change
 */
export function clearPermissionsCache() {
  permissionsCache.clear();
}

/**
 * Check if a user has permission to view domiciliation UI
 */
export async function checkDomiciliationPermission(specialToken = null) {
  try {
    // Case 1: Token-based permission check (call-center/anonymously/agente)
    if (specialToken) {
      return await checkWithToken(specialToken);
    }
    
    // Case 2: Client-based permission check (cliente)
    return await checkClientPermission();
    
  } catch (error) {
    console.error('[Domiciliation Permissions] Error checking permission:', error);
    
    // Return safe default (no permission) on error
    return {
      canView: false,
      userType: 'unknown',
      error: get(error, 'response.data.message', 'Error al verificar permisos')
    };
  }
}

/**
 * Check permission with special token
 * Used for call-center, anonymously, and agente users
 */
async function checkWithToken(token) {
  // Check cache first
  const cacheKey = `token:${token}`;
  if (permissionsCache.has(cacheKey)) {
    return permissionsCache.get(cacheKey);
  }
  
  const response = await axios.post(
    DOMICILIATION_PERMISSIONS_CHECK_SPECIAL,
    { token: token }
  );
  
  const result = {
    canView: get(response, 'data.data.canView', false),
    userType: get(response, 'data.data.userType', 'unknown')
  };
  
  // Cache the result (tokens are typically short-lived, so this is safe)
  permissionsCache.set(cacheKey, result);
  
  return result;
}

/**
 * Check permission for cliente users
 * Does not require token, just queries the DB directly
 */
async function checkClientPermission() {
  // Check cache first
  const cacheKey = `userType:${USER_TYPES.CLIENTE}`;
  if (permissionsCache.has(cacheKey)) {
    return permissionsCache.get(cacheKey);
  }
  
  const response = await axios.get(DOMICILIATION_PERMISSIONS_CLIENTE);
  
  const result = {
    canView: get(response, 'data.data.canView', false),
    userType: get(response, 'data.data.userType', USER_TYPES.CLIENTE)
  };
  
  // Cache the result (this rarely changes)
  permissionsCache.set(cacheKey, result);
  
  return result;
}

/**
 * Validate if a token looks like a valid JWT
 * This is just a basic check, real validation happens on the backend
 */
export function isValidTokenFormat(token) {
  if (!token || typeof token !== 'string') {
    return false;
  }
  
  // JWT format: header.payload.signature (3 parts separated by dots)
  const parts = token.split('.');
  return parts.length === 3;
}





// WEBPACK FOOTER //
// ./src/acl/domiciliation.js